<template>
  <StoryModal
    v-model:screenIndex="screenIndex"
    class="FinancialInsightsModalView"
    v-bind="{screens}"
    @close="handleClose"
  >
    <StoryScreen
      v-if="currentScreen"
      :key="currentScreen.title"
      :title="currentScreen.title"
      :titleCallouts="currentScreen.titleCallouts"
      :body="currentScreen.body"
      :titleCalloutColor="currentScreen.titleCalloutColor"
    >
      <slot name="figure">
        <FinancialInsightsMetricChart
          v-if="currentScreen.metric"
          v-model="currentScreen.metric"
        />
      </slot>

      <div class="FinancialInsightsModalView__actionList">
        <button
          v-if="currentScreen.explanation"
          data-testid="explanationButton"
          class="FinancialInsightsModalView__explanationButton"
          @click="handleExplanationClick"
        >
          <Icon
            size="16px"
            :name="IconName.Info"
          />

          <UIText
            weight="Medium"
            size="XSmall"
          >
            Learn more about this insight
          </UIText>
        </button>
      </div>

      <ul class="FinancialInsightsModalView__questionList">
        <UIText
          class="FinancialInsightsModalView__questionListTitle"
          tag="h3"
          size="3XSmall"
          casing="Upper"
        >
          Ask Era AI
        </UIText>

        <li
          v-for="question in currentScreen.questions"
          :key="question.Id"
          class="FinancialInsightsModalView__questionListItem"
        >
          <ChatQuestionCard
            :modelValue="question"
            :isLoading="isCreatingChat && currentQuestionId === question.Id"
            @click="handleQuestionClick(question)"
          />
        </li>
      </ul>

      <Teleport :to="PortalId.Body">
        <Transition>
          <SheetModal
            v-if="isExplanationSheetOpen && formattedExplanation"
            class="FinancialInsightsModalView__explanationSheet"
            theme="White"
            data-testid="explanationSheet"
            @close="handleExplanationSheetClose"
          >
            <div class="FinancialInsightsModalView__explanationSheetContainer">
              <Icon
                :name="IconName.Info"
              />
              <!-- eslint-disable vue/no-v-html -->
              <UIText
                tag="div"
                class="FinancialInsightsModalView__explanationSheetContent"
                isMultiline
              >
                <div v-html="formattedExplanation" />

                <div
                  class="FinancialInsightsModalView__calculationNote"
                  v-html="formattedCalculationNote"
                />
              </UIText>
            <!-- eslint-enable vue/no-v-html -->
            </div>
          </SheetModal>
        </Transition>
      </Teleport>
    </StoryScreen>
  </StoryModal>
</template>

<script lang="ts" setup>
import ChatQuestionCard from "@cosine/components/ChatQuestionCard.vue";
import FinancialInsightsMetricChart from "@cosine/components/FinancialInsightsMetricChart.vue";
import { IconName } from "@cosine/components/Icon.types";
import Icon from "@cosine/components/Icon.vue";
import { PortalId } from "@cosine/components/Portal.types";
import SheetModal from "@cosine/components/SheetModal.vue";
import StoryModal from "@cosine/components/StoryModal.vue";
import StoryScreen from "@cosine/components/StoryScreen.vue";
import UIText from "@cosine/components/UIText.vue";
import useChat from "@cosine/composables/useChat";
import useFinancialTimelineEntry from "@cosine/composables/useFinancialTimelineEntry";
import { FinancialEntryPayloadTypeName } from "@cosine/composables/useFinancialTimelineEntry.types";
import useModalRoute from "@cosine/composables/useModalRoute";
import useModalRouter from "@cosine/composables/useModalRouter";
import useRequest from "@cosine/composables/useRequest";
import { IFinancialInsightsCommonQuestion, IFinancialInsightsMetric, IFinancialInsightsSnapshot } from "@cosine/lib/api-models";
import { RouteName } from "@cosine/routes";
import { IFinancialInsightsStoryScreen } from "@cosine/views/FinancialInsightsModalView.types";
import { marked } from "marked";
import { computed, ref, watch } from "vue";

const {
  timelineEntryId,
} = defineProps<{
  timelineEntryId: string,
}>();

const isExplanationSheetOpen = ref(false);

const {
  openModal,
  replaceModal,
  closeActiveModal,
} = useModalRouter();

const {
  modalRoute,
} = useModalRoute(RouteName.insightsView);

const {
  createNewChatWithMessage,
} = useChat();

const {
  handleRequest,
  isRequesting: isCreatingChat,
} = useRequest();

const screenIndex = ref(parseInt(modalRoute.value?.params.screenIndex?.toString() || "0", 10));
const currentQuestionId = ref<string>();

const {
  snapshot,
} = useFinancialTimelineEntry(timelineEntryId, FinancialEntryPayloadTypeName.FinancialInsightsPayload);

const metrics = computed((): Record<string, IFinancialInsightsMetric> => {
  if (!snapshot.value) return {};

  const {
    Metrics,
  } = snapshot.value as IFinancialInsightsSnapshot;

  if (!Metrics) return {};

  return Metrics.reduce((acc, metric) => ({
    ...acc,
    [metric.Id]: metric,
  }), {});
});

const screens = computed((): Array<IFinancialInsightsStoryScreen> => {
  if (!snapshot.value) return [];

  const {
    Authors,
    Highlights,
    Lowlights,
    Meta,
  } = snapshot.value as IFinancialInsightsSnapshot;

  return [
    ...Highlights.map((highlight) => ({
      label: "Strength",
      titleCalloutColor: "var(--colorSwissTeal300)",
      ...highlight,
    })),
    ...Lowlights.map((lowlight) => ({
      label: "Opportunity",
      titleCalloutColor: "var(--colorSwissPurple300)",
      ...lowlight,
    })),
  ].map<IFinancialInsightsStoryScreen>((highlowlight) => {
    const metricId = highlowlight.MetricId;
    const metric = metricId ? metrics.value[metricId] : undefined;
    const author = Authors.find((_author) => _author.Role === highlowlight.AuthorRole);

    return {
      author: {
        name: (author || Authors.at(0))?.DisplayName || "Era",
      },
      label: highlowlight.label,
      title: highlowlight.Title,
      titleCallouts: highlowlight.TitleEmphasis ? [highlowlight.TitleEmphasis] : [],
      titleCalloutColor: highlowlight.titleCalloutColor,
      body: highlowlight.Explanation,
      questions: highlowlight.Questions || [],
      timestamp: Meta.GeneratedAt,
      metric,
      explanation: metric?.Commentary,
      calculationNote: metric?.CalculationNote,
    };
  });
});

const currentScreen = computed(() => screens.value[screenIndex.value]);

const formattedExplanation = computed(() => {
  if (!currentScreen.value.explanation) return;

  return marked(currentScreen.value.explanation);
});

const formattedCalculationNote = computed(() => {
  if (!currentScreen.value.calculationNote) return;
  return marked(currentScreen.value.calculationNote);
});

watch(screenIndex, () => {
  replaceModal({
    name: RouteName.insightsView,
    params: {
      timelineEntryId,
      screenIndex: screenIndex.value,
    },
  });
});

function handleClose () {
  closeActiveModal();
}

async function handleQuestionClick (question: IFinancialInsightsCommonQuestion) {
  if (isCreatingChat.value) return;

  currentQuestionId.value = question.Id;

  const [, id] = await handleRequest(createNewChatWithMessage(question.QuestionText), {
    showOnlyErrors: true,
  });

  if (id) {
    await openModal({
      name: RouteName.threadsView,
      params: {
        id,
      },
      state: {
        shouldAutofocus: false,
      },
    });
  }
}

function handleExplanationClick () {
  isExplanationSheetOpen.value = true;
}

function handleExplanationSheetClose () {
  isExplanationSheetOpen.value = false;
}
</script>

<style lang="scss" scoped>
.FinancialInsightsModalView {
  // TODO: find a more scalable way to do this
  :deep(.SheetModal__closeButton) {
    top: 22px;
    right: 4px;
  }

  :deep(.SheetModal__closeButtonIcon) {
    width: 20px;
    height: 20px;
  }
}

.FinancialInsightsModalView__storyNavList {
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
}

.FinancialInsightsModalView__storyNavItem {
  height: 2px;
  border-radius: 4px;
  background-color: var(--colorSwissGrey200);

  &:first-child {
    background-color: var(--colorSwissBlack);
  }
}

.FinancialInsightsModalView__questionList {
  display: grid;
  gap: 16px;
  padding-top: 20px;

  border-top: 1px solid var(--themeColorBorder);
}

.FinancialInsightsModalView__questionListTitle {
  color: var(--colorSwissAlphaBlack600);
  font-feature-settings: "ss05";
}

.FinancialInsightsModalView__explanationButton {
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 4px 10px 4px 5px;

  border-radius: 9999px;
  background-color: var(--themeColorBackgroundOffset);
}

.FinancialInsightsModalView__explanationSheetContainer {
  display: grid;
  gap: 24px;
  padding: 24px;
}

.FinancialInsightsModalView__explanationSheetContent {
  display: grid;
  gap: 24px;
}

.FinancialInsightsModalView__calculationNote {
  color: var(--themeColorTextSubdued);
}
</style>
